import AuthProvider from '../AuthProvider'
import DoggosLoading from '../DoggosLoading'
import { MDXProvider } from '@mdx-js/react'
import { useLocation } from '@reach/router'
import * as React from 'react'
import { Provider as ReakitProvider } from 'reakit'

const SERVER_SIDE_LOCATION = {}

/**
 * Only `/search` needs the global Algolia InstantSearch provider.
 * Homepage featured listings mount their own InstantSearch below the fold.
 * Blog posts, listings, categories, and other static pages must not wait
 * on InstantSearch (avoids a brief "Loading" flash on first paint).
 */
function needsGlobalInstantSearch(pathname = '') {
  const p = String(pathname || '')
  return p === '/search' || p.startsWith('/search/')
}

function AppShell({ children }) {
  return (
    <MDXProvider>
      <AuthProvider>
        <ReakitProvider>{children}</ReakitProvider>
      </AuthProvider>
    </MDXProvider>
  )
}

function App(props) {
  const location = useLocation()
  const useSearch = needsGlobalInstantSearch(location.pathname)
  const [SearchRoot, setSearchRoot] = React.useState(null)
  const [searchState, setSearchState] = React.useState(SERVER_SIDE_LOCATION)

  React.useEffect(() => {
    if (!useSearch) return undefined
    let cancelled = false
    void import('./InstantSearchRoot').then((mod) => {
      if (cancelled) return
      setSearchRoot(() => mod.default)
      setSearchState(mod.locationToSearchState(location))
    })
    return () => {
      cancelled = true
    }
    // Only boot InstantSearch when the route needs it — not on every location change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location snapshotted at provider mount
  }, [useSearch])

  // /search URL ↔ InstantSearch sync lives in InstantSearchRoot so it can
  // ignore echoes of its own debounced navigates (avoids check→uncheck races).

  const shell = <AppShell>{props.children}</AppShell>

  // Public / CRM routes: no Algolia provider.
  if (!useSearch) return shell

  // /search uses InstantSearch connectors — do not render page widgets until
  // the provider is ready (avoids store.getState crashes).
  if (!SearchRoot) {
    return (
      <AppShell>
        <DoggosLoading aria-label="Loading search" layout="inset" />
      </AppShell>
    )
  }

  return (
    <SearchRoot
      location={location}
      searchState={searchState}
      setSearchState={setSearchState}
    >
      {shell}
    </SearchRoot>
  )
}

export default App
